import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Tabs,
  Tab,
} from "@material-ui/core";
import { Refresh } from "@material-ui/icons";
import climaService from "@services/climaService";
import TCLoading from "@components/TCLoading";
import formatDate from "@utils/formatDate";
import { Watermark } from "../components/Dashboard/Contratos";
import statesEnum from "../utils/enums/statesEnum";
import { makeStyles } from "@material-ui/core/styles";
import blue from "@material-ui/core/colors/blue";

import mixpanel from "@utils/mixpanel/mixpanel.js";
import { RELATORIO_PREVISOES_CLIMATICAS } from "../utils/mixpanel/mixpanelEvents";

const useStyles = (pTheme) =>
  makeStyles((theme) => ({
    tabsRoot: {
      minHeight: 32,
      marginTop: 8,
    },
    tabRoot: {
      minHeight: 32,
      minWidth: 80,
      textTransform: "none",
      fontWeight: 500,
      color: pTheme === "DARK" ? "white" : "black",
      "&$tabSelected": {
        color: pTheme === "DARK" ? "white" : "black",
        fontWeight: 600,
        borderBottom: `2px solid ${pTheme === "DARK" ? "white" : "black"}`,
      },
    },
    tabSelected: {},
  }));

let { Regiao, Estado } = statesEnum; // adicionamos Estado aqui

const getTemperatureColorFont = (temp) => {
  if (typeof temp !== "number") return "inherit";
  if (temp < 3) return "#dc2626";
  if (temp >= 3 && temp <= 5) return "#ffa500";

  const minTemp = 5;
  const maxTemp = 20;
  const normalized = Math.min((temp - minTemp) / (maxTemp - minTemp), 1);
  const start = [167, 243, 208];
  const end = [4, 120, 87];
  const r = Math.round(start[0] + (end[0] - start[0]) * normalized);
  const g = Math.round(start[1] + (end[1] - start[1]) * normalized);
  const b = Math.round(start[2] + (end[2] - start[2]) * normalized);

  return `rgb(${r},${g},${b})`;
};

const getPrecipitationColorFont = (value) => {
  if (typeof value !== "number") return "inherit";
  if (value === 0) return "inherit";
  if (value < 2) return blue[200];
  if (value < 10) return blue[400];
  if (value < 25) return blue[500];
  return blue[800];
};
const ClimaPage = () => {
  const [theme] = useSelector((states) => [states.authReducer.theme]);
  const classes = useStyles(theme)();
  const [climas, setClimas] = useState([]);
  const [climaUpdate, setClimaUpdate] = useState(``);
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [dataTypeTabIndex, setDataTypeTabIndex] = useState(0); // 0 = temp, 1 = precipitação

  const fetchClimas = useCallback(() => {
    setLoading(true);
    climaService
      .getClimas()
      .then((res) => setClimas(res))
      .finally(() => setLoading(false));
    climaService
      .getClimaUpdateInfo()
      .then((res) => setClimaUpdate(res.modelrun_updatetime_utc))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    mixpanel.track(RELATORIO_PREVISOES_CLIMATICAS);
    fetchClimas();
  }, [fetchClimas]);

  const estados = climas.reduce((acc, clima) => {
    const state = clima.state || "Outros";
    const region = clima.region || "Outros";
    if (!acc[state]) acc[state] = {};
    if (!acc[state][region]) acc[state][region] = [];
    acc[state][region].push(clima);
    return acc;
  }, {});

  const stateKeys = Object.keys(estados).sort();

  const renderRegionTable = (cidadeList, regionLabel, dataType) => {
    const rawDias = cidadeList[0]?.time || [];
    const dataKey = dataType === 0 ? "temperature_min" : "precipitation";

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const validIndices = rawDias
      .map((dia, idx) => {
        const diaDate = new Date(`${dia}T00:00:00`);
        return diaDate >= today ? idx : null;
      })
      .filter((idx) => idx !== null);

    const dias = validIndices.map((i) => rawDias[i]);

    return (
      <Box mt={4} key={regionLabel}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 600, marginBottom: 8 }}
          >
            {Regiao[regionLabel] || regionLabel}{" "}
          </Typography>
          <Typography variant="caption">
            {` Atualizado em ${formatDate(climaUpdate + `Z`)} `}
          </Typography>
        </div>
        <TableContainer component={Paper} style={{ position: "relative" }}>
          <Watermark />
          <Table size="small" style={{ position: "relative", zIndex: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Município</TableCell>
                {dias.map((dia, idx) => (
                  <TableCell key={idx} align="right">
                    {formatDate(dia + `T05:00:00.000Z`, false, true)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...cidadeList]
                .sort((a, b) => a.city.localeCompare(b.city))
                .map((cidade) => (
                  <TableRow key={cidade.city}>
                    <TableCell>{cidade.city}</TableCell>
                    {validIndices.map((i) => {
                      const value = cidade[dataKey]?.[i];
                      return (
                        <TableCell
                          key={i}
                          align="right"
                          style={{
                            color:
                              dataType === 0
                                ? getTemperatureColorFont(value)
                                : getPrecipitationColorFont(value),
                            fontWeight: 600,
                          }}
                        >
                          {typeof value === "number" ? value.toFixed(1) : "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Container component="main" maxWidth="md">
      <Box
        mt={3}
        display="flex"
        flexDirection="column"
        gap={4}
        alignItems="center"
        position="relative"
      >
        {/* Botão de refresh no topo direito absoluto */}
        <div style={{ gap: 12, alignItems: "center", display: "flex" }}>
          <Typography variant="h6" align="center">
            🌦️ Previsões Climáticas
          </Typography>
          <IconButton onClick={fetchClimas}>
            <Refresh fontSize="small" />
          </IconButton>

          {/* Título centralizado */}
        </div>

        {/* Tabs para tipo de dado centralizados */}
        <Tabs
          value={dataTypeTabIndex}
          onChange={(e, newValue) => {
            setDataTypeTabIndex(newValue);
          }}
          classes={{ root: classes.tabsRoot }}
          TabIndicatorProps={{ style: { display: "none" } }}
        >
          <Tab
            label="🌡️ Temperatura Mínima (°C)"
            classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
          />
          <Tab
            label="🌧️ Precipitação (mm)"
            classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
          />
        </Tabs>

        {/* Tabs por estado */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <TCLoading size={40} />
          </Box>
        ) : (
          <>
            <Tabs
              value={tabIndex}
              onChange={(e, newValue) => setTabIndex(newValue)}
              classes={{ root: classes.tabsRoot }}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{ style: { display: "none" } }}
            >
              {stateKeys.map((state, idx) => (
                <Tab
                  key={state}
                  label={Estado[state] || state}
                  classes={{
                    root: classes.tabRoot,
                    selected: classes.tabSelected,
                  }}
                />
              ))}
            </Tabs>
            {stateKeys.map((state, idx) => (
              <Box key={state} hidden={tabIndex !== idx}>
                {tabIndex === idx &&
                  Object.entries(estados[state]).map(([region, cidades]) =>
                    renderRegionTable(cidades, region, dataTypeTabIndex),
                  )}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Container>
  );
};

export default ClimaPage;
